import { AlertTriangle, LoaderCircle, RotateCw, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cacheBustedLocation,
  disruptedProbeDelay,
  type UiTransportSample,
  UI_RECOVERED_RTT_MS,
  UI_SLOW_RTT_MS,
  UI_TRANSPORT_EVENT,
} from '../lib/ui-availability';

type AvailabilityMode = 'healthy' | 'checking-slow' | 'slow' | 'offline' | 'restarting' | 'recovering';

interface AvailabilityPayload {
  status: 'ok' | 'restarting';
  service: 'ui';
  instance_id: string;
}

const HEALTHY_PROBE_MS = 30_000;
const SLOW_PROBE_MS = 5_000;
const PROBE_TIMEOUT_MS = 4_000;

export function UiAvailabilityGuard() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AvailabilityMode>('healthy');
  const [latency, setLatency] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const modeRef = useRef(mode);
  const slowSamples = useRef(0);
  const healthySamples = useRef(0);
  const probing = useRef(false);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const observe = useCallback((sample: UiTransportSample) => {
    setLatency(Math.round(sample.latencyMs));
    if (!sample.ok) {
      slowSamples.current = 0;
      healthySamples.current = 0;
      setMode('offline');
      return;
    }

    if (sample.latencyMs >= UI_SLOW_RTT_MS) {
      healthySamples.current = 0;
      slowSamples.current += 1;
      if (slowSamples.current >= 2) setMode('slow');
      else if (modeRef.current === 'healthy') setMode('checking-slow');
      return;
    }

    slowSamples.current = 0;
    if (modeRef.current === 'checking-slow') {
      setMode('healthy');
      return;
    }
    if (modeRef.current === 'slow' && sample.latencyMs <= UI_RECOVERED_RTT_MS) {
      healthySamples.current += 1;
      if (healthySamples.current >= 2) setMode('healthy');
    }
  }, []);

  const probe = useCallback(async () => {
    if (probing.current) return;
    probing.current = true;
    const started = performance.now();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    try {
      // cache:'no-store' + no-store hlavičky serveru — query cache-buster
      // není potřeba a fixní literál drží presentation-boundary kontrakt.
      const response = await fetch('/ui/availability', {
        cache: 'no-store',
        credentials: 'include',
        signal: controller.signal,
      });
      const payload = response.ok ? await response.json() as AvailabilityPayload : null;
      const elapsed = performance.now() - started;
      if (!payload || payload.service !== 'ui') throw new Error('UI availability probe failed');
      if (payload.status === 'restarting') {
        healthySamples.current = 0;
        setMode('restarting');
        setAttempt((value) => value + 1);
      } else if (modeRef.current === 'offline' || modeRef.current === 'restarting' || modeRef.current === 'recovering') {
        healthySamples.current += 1;
        setAttempt(0);
        setMode('recovering');
        if (healthySamples.current >= 2) {
          window.location.replace(cacheBustedLocation(window.location.href));
        }
      } else {
        observe({ ok: true, latencyMs: elapsed });
      }
    } catch {
      healthySamples.current = 0;
      setAttempt((value) => value + 1);
      if (modeRef.current !== 'restarting') setMode('offline');
    } finally {
      window.clearTimeout(timeout);
      probing.current = false;
    }
  }, [observe]);

  useEffect(() => {
    const onTransport = (event: Event) => observe((event as CustomEvent<UiTransportSample>).detail);
    const onOffline = () => {
      healthySamples.current = 0;
      setMode('offline');
    };
    const onOnline = () => void probe();
    window.addEventListener(UI_TRANSPORT_EVENT, onTransport);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener(UI_TRANSPORT_EVENT, onTransport);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [observe, probe]);

  useEffect(() => {
    const events = new EventSource('/ui/availability/events', { withCredentials: true });
    events.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as AvailabilityPayload;
        if (payload.status === 'restarting') {
          healthySamples.current = 0;
          setMode('restarting');
        } else if (
          modeRef.current === 'offline' || modeRef.current === 'restarting' ||
          modeRef.current === 'recovering'
        ) {
          void probe();
        }
      } catch {
        // Poškozený event nesmí měnit dostupnost; ověří ji další probe.
      }
    };
    events.onerror = () => void probe();
    return () => events.close();
  }, [probe]);

  useEffect(() => {
    const disrupted = mode === 'offline' || mode === 'restarting' || mode === 'recovering';
    if (disrupted) {
      const timer = window.setTimeout(() => void probe(), disruptedProbeDelay(attempt));
      return () => window.clearTimeout(timer);
    }
    const delay = mode === 'healthy' ? HEALTHY_PROBE_MS : SLOW_PROBE_MS;
    const timer = window.setInterval(() => void probe(), delay);
    return () => window.clearInterval(timer);
  }, [attempt, mode, probe]);

  const blocked = mode === 'offline' || mode === 'restarting' || mode === 'recovering';
  useEffect(() => {
    const app = document.querySelector<HTMLElement>('[data-acm-app-content]');
    if (app) app.inert = blocked;
    document.body.dataset.acmAvailability = mode;
    return () => {
      if (app) app.inert = false;
      delete document.body.dataset.acmAvailability;
    };
  }, [blocked, mode]);

  if (mode === 'healthy' || mode === 'checking-slow') return null;
  if (mode === 'slow') {
    return (
      <div role='status' className='fixed left-1/2 top-4 z-[10000] -translate-x-1/2 rounded-xl border border-amber-400/30 bg-slate-950/95 px-4 py-3 text-slate-100 shadow-2xl backdrop-blur'>
        <div className='flex items-center gap-3'>
          <AlertTriangle className='h-5 w-5 shrink-0 text-amber-400' />
          <div>
            <div className='text-sm font-semibold'>{t('availability.slowTitle')}</div>
            <div className='text-xs text-slate-400'>{t('availability.slowBody', { latency })}</div>
          </div>
        </div>
      </div>
    );
  }

  const restarting = mode === 'restarting';
  const recovering = mode === 'recovering';
  const Icon = restarting ? RotateCw : recovering ? LoaderCircle : WifiOff;
  return (
    // Rozmazání zůstává — jen se PŘESTALO PŘEKRESLOVAT.
    //
    // Filip 2026-08-30: „nereagoval prohlížeč na kliknutí ani na jiné záložky."
    // Nebylo to blokování ovládání; `inert` i z-index se drží ve své záložce.
    // Vinu nesla skladba vrstev: `backdrop-blur` ležel na `fixed inset-0`
    // a UVNITŘ se točilo `animate-spin`. Potomek, který se hýbe, invaliduje
    // vrstvu, na které leží — takže každý snímek animace vynutil nové rozmazání
    // CELÉHO viewportu. GPU proces je v prohlížeči sdílený napříč záložkami,
    // takže to položilo i stránky, které s ACM nemají nic společného.
    //
    // Sedí to i s tím, že se to dřív nedělo: ikona se točí JEN v režimu
    // `restarting`/`recovering`. Při obyčejném výpadku je statická — a
    // `restarting` je přesně to, co se ukazuje při nasazení.
    //
    // Řešení není blur zahodit, ale oddělit ho od pohyblivého obsahu:
    //   vrstva 1 — rozmazané pozadí BEZ potomků; nic ji neinvaliduje
    //   vrstva 2 — dialog jako SOUROZENEC, ne potomek; `transform-gpu` mu dá
    //              vlastní kompozitní vrstvu a `contain: paint` uzavře
    //              překreslování dovnitř
    // Pozadí se tak spočítá jednou a ikona se točí ve svém malém obdélníku.
    <div className='fixed inset-0 z-[10000]' role='alertdialog' aria-modal='true' aria-live='assertive'>
      {/* Rozmazané pozadí — záměrně bez jediného potomka. */}
      <div aria-hidden className='absolute inset-0 bg-slate-950/70 backdrop-blur-sm' />
      <div className='absolute inset-0 flex items-center justify-center p-5'>
        <div className='w-full max-w-md transform-gpu rounded-2xl border border-white/10 bg-slate-950 p-7 text-center text-slate-100 shadow-2xl [contain:paint]'>
        <div className='mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-blue-400/20 bg-blue-500/10'>
          <Icon className={`h-8 w-8 text-blue-400 ${restarting || recovering ? 'animate-spin' : ''}`} />
        </div>
        <h2 className='mb-2 text-xl font-semibold'>
          {t(restarting ? 'availability.restartTitle' : recovering ? 'availability.recoveringTitle' : 'availability.offlineTitle')}
        </h2>
        <p className='mb-5 text-sm leading-6 text-slate-400'>
          {t(restarting ? 'availability.restartBody' : recovering ? 'availability.recoveringBody' : 'availability.offlineBody')}
        </p>
        <div className='text-xs text-slate-500'>
          {!navigator.onLine ? t('availability.browserOffline') : t('availability.checking')}
          {attempt > 0 ? ` · ${t('availability.attempt', { count: attempt })}` : ''}
        </div>
        </div>
      </div>
    </div>
  );
}
